// The brand: colors, logo, links.
import { BRAND_LINKS, type Settings as SettingsShape, TRADE_LOOKS } from "@steward/shared";
import { useState } from "react";
import { useSettings, useUpdateCompany, useUpdateSettings, useUploadLogo } from "../../api/hooks";
import { useSession } from "../../app/session";
import { InputText } from "../../components/fields";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, Details } from "../../components/parts";
import { errorText, useAddressOpen } from "./common";

/**
 * The brand kit's colors, in the order Settings shows them. Only the brand color is a must. The other three change what
 * clients see (lookFor in @steward/shared client-look.ts); each left empty uses the trade's built-in one.
 */
export const BRAND_COLORS = [
  ["brandColor", "Brand color", "The app's own buttons and the band on the sign in page."],
  ["accentColor", "Accent color", "The top band and links on pay and quote pages, emails and invoices."],
  ["secondaryColor", "Secondary color", "The bottom band on pay and quote pages, emails and invoices."],
  ["ctaColor", "Call to action color", "The main button clients press, like Pay and Approve."],
] as const;

type BrandColorKey = (typeof BRAND_COLORS)[number][0];

/** The trade's own color for a part of the kit, used when the brand leaves it empty. */
export function builtIn(trade: string, key: Exclude<BrandColorKey, "brandColor">): string {
  const look = TRADE_LOOKS[(trade in TRADE_LOOKS ? trade : "hq") as keyof typeof TRADE_LOOKS];
  return { accentColor: look.accent, secondaryColor: look.menu, ctaColor: look.cta }[key];
}

const isHex = (c: string) => /^#[0-9a-f]{6}$/i.test(c);

/** A logo box: drop a picture on it or click to pick one. It saves the moment it is picked. */
function LogoDrop({
  label,
  hint,
  url,
  onBrand,
  busy,
  onFile,
  onRemove,
}: {
  label: string;
  hint: string;
  url: string;
  /** The white logo is shown on the brand color so it can be seen. */
  onBrand?: string;
  busy: boolean;
  onFile: (f: File | undefined) => void;
  onRemove: () => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div className="logo-pick">
      <b>{label}</b>
      <label
        className={`logo-drop${over ? " over" : ""}`}
        // The color logo is shown on white, as clients see it, even in dark mode.
        style={onBrand ? { background: onBrand } : url ? { background: "#fff" } : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!busy) onFile(e.dataTransfer.files[0]);
        }}
      >
        {url ? (
          <img src={url} alt={label} />
        ) : (
          <span className={onBrand ? "on-brand" : "muted"}>
            {busy ? "Uploading..." : "Drop a logo here or click to pick one"}
          </span>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          disabled={busy}
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>
      <p className="muted small">
        {hint}
        {url ? (
          <>
            {" "}
            <button type="button" className="link" onClick={onRemove}>
              Remove
            </button>
          </>
        ) : null}
      </p>
    </div>
  );
}

export function BrandEdit({ onClose }: { onClose: () => void }) {
  const { company } = useSession();
  const save = useUpdateCompany();
  const toast = useToast();
  const addressOpen = useAddressOpen();
  const upload = useUploadLogo();
  const [colors, setColors] = useState<Record<BrandColorKey, string>>({
    brandColor: company.brandColor ?? "#0B4F55",
    ctaColor: company.ctaColor ?? "",
    secondaryColor: company.secondaryColor ?? "",
    accentColor: company.accentColor ?? "",
  });
  const setColor = (k: BrandColorKey, v: string) => setColors((c) => ({ ...c, [k]: v }));
  // A logo saves the moment it is picked; Save here only sends it when it was removed.
  const [logo, setLogo] = useState(company.logoUrl ?? "");
  const [white, setWhite] = useState(company.logoWhiteUrl ?? "");
  const [saved, setSaved] = useState({ color: company.logoUrl ?? "", white: company.logoWhiteUrl ?? "" });
  const [uploading, setUploading] = useState<"color" | "white" | null>(null);
  const [address, setAddress] = useState(company.appUrl ?? "");
  const [clientAddress, setClientAddress] = useState(company.clientUrl ?? "");
  const [error, setError] = useState<string | null>(null);
  const pickLogo = async (variant: "color" | "white", f: File | undefined) => {
    if (!f) return;
    setError(null);
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      setError("Use a PNG, JPG or WebP picture.");
      return;
    }
    if (f.size > 2 * 1_048_576) {
      setError("That picture is too big. The most is 2 MB.");
      return;
    }
    const dataBase64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === "string" ? (r.result.split(",")[1] ?? "") : "");
      r.onerror = () => reject(new Error("That picture could not be read."));
      r.readAsDataURL(f);
    });
    setUploading(variant);
    upload.mutate(
      { name: f.name, mime: f.type, dataBase64, variant },
      {
        onSuccess: (c) => {
          if (variant === "white") setWhite(c.logoWhiteUrl ?? "");
          else setLogo(c.logoUrl ?? "");
          setSaved({ color: c.logoUrl ?? "", white: c.logoWhiteUrl ?? "" });
          toast(variant === "white" ? "White logo saved" : "Logo saved");
        },
        onError: (e) => setError(errorText(e)),
        onSettled: () => setUploading(null),
      },
    );
  };
  const badColor = BRAND_COLORS.find(([k]) => colors[k] !== "" && !isHex(colors[k]));
  return (
    <Modal
      title="Edit brand"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={uploading !== null}
            onClick={() => {
              if (badColor) {
                setError(`Check the ${badColor[1].toLowerCase()}. Use a color like #1F6F5C.`);
                return;
              }
              save.mutate(
                {
                  id: company.id,
                  patch: {
                    brandColor: colors.brandColor,
                    ctaColor: colors.ctaColor || null,
                    secondaryColor: colors.secondaryColor || null,
                    accentColor: colors.accentColor || null,
                    ...(logo !== saved.color ? { logoUrl: logo || null } : {}),
                    ...(white !== saved.white ? { logoWhiteUrl: white || null } : {}),
                    // Only sent by head office, so a brand owner saving their color never trips the address lock.
                    ...(addressOpen ? { appUrl: address.trim() || null, clientUrl: clientAddress.trim() || null } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    toast("Brand saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              );
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <div className="brand-colors">
        {BRAND_COLORS.map(([key, label, about]) => (
          <InputText
            key={key}
            label={label}
            value={colors[key]}
            onChange={(e) => setColor(key, e.target.value)}
            placeholder={key === "brandColor" ? undefined : `${builtIn(company.trade, key)} built in`}
            description={about}
            prefix={
              <input
                type="color"
                aria-label={`Pick the ${label.toLowerCase()}`}
                value={isHex(colors[key]) ? colors[key] : key === "brandColor" ? "#000000" : builtIn(company.trade, key)}
                onChange={(e) => setColor(key, e.target.value)}
              />
            }
          />
        ))}
      </div>
      <div className="logo-pair">
        <LogoDrop
          label="Logo"
          hint="For white backgrounds. Used on the color band only when there is no white logo."
          url={logo}
          busy={uploading === "color"}
          onFile={(f) => void pickLogo("color", f)}
          onRemove={() => setLogo("")}
        />
        <LogoDrop
          label="White logo"
          hint="All white. Sits on the top band of pay and quote pages, emails, invoices and the sign in page."
          url={white}
          onBrand={isHex(colors.accentColor) ? colors.accentColor : builtIn(company.trade, "accentColor")}
          busy={uploading === "white"}
          onFile={(f) => void pickLogo("white", f)}
          onRemove={() => setWhite("")}
        />
      </div>
      <p className="muted small">
        PNG, JPG or WebP, up to 2 MB each. A wide logo on a clear background looks best. Both are used at every location of the
        brand.
      </p>
      <InputText
        label="Web address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={company.linkUrl}
        disabled={!addressOpen}
        description={
          addressOpen
            ? "Where this brand's office and crew sign in. Leave it empty to use the one above."
            : "Head office sets this."
        }
      />
      <InputText
        label="Client web address"
        value={clientAddress}
        onChange={(e) => setClientAddress(e.target.value)}
        placeholder={company.clientLinkUrl}
        disabled={!addressOpen}
        description={
          addressOpen
            ? "Where clients open pay links, quotes, reviews and their hub. Leave it empty to use the web address."
            : "Head office sets this."
        }
      />
    </Modal>
  );
}

/** The brand's links at the bottom of the pay and quote pages (old Steward "Social networks"). */
export function BrandLinksCard() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<SettingsShape["brand"]["links"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!s.data) return null;
  const links = s.data.settings.brand.links;
  const owner = can("settings.update");
  const bad = draft ? BRAND_LINKS.filter(([k]) => draft[k].trim() && !/^https:\/\/\S+$/.test(draft[k].trim())) : [];
  return (
    <Card
      id="links"
      title="Links on client pages"
      action={
        owner && !draft ? (
          <Button type="secondary" size="small" icon="edit" onClick={() => setDraft({ ...links })}>
            Edit
          </Button>
        ) : null
      }
      flush={!draft}
    >
      {draft ? (
        <div className="stack">
          {error ? <Banner type="error">{error}</Banner> : null}
          <div className="form-grid">
            {BRAND_LINKS.map(([k, label]) => (
              <InputText
                key={k}
                label={label}
                type="url"
                placeholder="https://"
                value={draft[k]}
                error={bad.some(([b]) => b === k) ? "Start with https://" : null}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
              />
            ))}
          </div>
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={bad.length > 0}
              onClick={() =>
                save.mutate(
                  { brand: { links: Object.fromEntries(BRAND_LINKS.map(([k]) => [k, draft[k].trim()])) } },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      toast("Links saved");
                    },
                    onError: (e) => setError(errorText(e)),
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Details rows={BRAND_LINKS.map(([k, label]) => [label, links[k] || null])} />
          <p className="muted small card-note">Clients see the filled ones at the bottom of pay and quote pages.</p>
        </>
      )}
    </Card>
  );
}
